import { isMonitoredAssetSecurity } from '@src/components/AutoHedging/crossAssetRiskGroup';
import { isCrossAssetRiskGroupLinearDerivativeSecurity } from '@src/containers/Admin/TradingLimits/CrossAssetRiskGroups/utils';
import type { CrossAssetRiskGroup } from '@src/containers/Admin/TradingLimits/SubAccountTradingLimits/crossAssetRiskGroupsApi';
import type { Currency, Security } from '@talos/kyoko';
import type { HedgeMonitoredAsset } from './types';

export function isAllAssetsHedgeGroup(
  group: CrossAssetRiskGroup | undefined,
  emptyGroupMeansAllAssets: boolean
): boolean {
  if (!group || !emptyGroupMeansAllAssets) {
    return false;
  }

  const groupAssets = group.Assets?.filter((groupAsset): groupAsset is string => Boolean(groupAsset)) ?? [];
  return groupAssets.length === 0;
}

export function getHedgeSecurityOptions(
  asset: HedgeMonitoredAsset | undefined,
  group: CrossAssetRiskGroup | undefined,
  searchableSecurities: Security[],
  securitiesBySymbol: Map<string, Security>,
  currenciesBySymbol: Map<string, Currency>,
  emptyGroupMeansAllAssets: boolean
): Security[] {
  if (!asset) {
    return [];
  }

  if (isMonitoredAssetSecurity(asset.Symbol, securitiesBySymbol, currenciesBySymbol)) {
    const security = securitiesBySymbol.get(asset.Symbol);
    return security ? [security] : [];
  }

  const exposureSecurities = searchableSecurities.filter(
    security =>
      security.PositionCurrency === asset.Symbol ||
      security.BaseCurrency === asset.Symbol ||
      security.QuoteCurrency === asset.Symbol
  );
  if (!group) {
    return exposureSecurities;
  }

  if (isAllAssetsHedgeGroup(group, emptyGroupMeansAllAssets)) {
    return searchableSecurities.filter(
      security => isCrossAssetRiskGroupLinearDerivativeSecurity(security) && security.PositionCurrency === asset.Symbol
    );
  }

  const searchableSymbols = new Set(searchableSecurities.map(security => security.Symbol));
  return (group.Assets?.filter((groupAsset): groupAsset is string => Boolean(groupAsset)) ?? [])
    .map(groupAsset => securitiesBySymbol.get(groupAsset))
    .filter((security): security is Security => security != null && searchableSymbols.has(security.Symbol));
}
